import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  User, Phone, Save, Calendar as CalendarIcon, ChevronLeft, ChevronRight,
  Plus, Search, Trash2, Pencil, X, CheckCircle2, Loader2, FileText, Euro, Wallet
} from 'lucide-react';
import { calculerPaie, dirhams, pourcent, TAUX_MAD } from './FormuleAssu';

// Espace personnel d'un candidat dont le dossier a été validé.
// Deux choses, et rien d'autre : ses informations, qu'il tient à jour, et ses
// contrats, qu'il saisit au jour le jour dans un calendrier. Le total du mois
// est le chiffre d'affaires que l'administration reprend pour sa paie.

const NAVY = '#2d3f7f';
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const JOURS = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];

const euros = (n) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 2
}).format(Number(n) || 0);

// Les dates arrivent en « aaaa-mm-jj… » : on les lit telles quelles, sans
// repasser par `new Date`, qui décalerait d'un jour à l'ouest de Greenwich.
const jourDe = (valeur) => String(valeur || '').slice(0, 10);
const enFrancais = (valeur) => {
  const [a, m, j] = jourDe(valeur).split('-');
  return j ? `${j}/${m}/${a}` : '';
};
const cleMois = (annee, mois) => `${annee}-${String(mois + 1).padStart(2, '0')}`;
const dateDuJour = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function EspaceCandidat({ currentUser }) {
  const API_URL = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('token');
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const aujourdhui = new Date();
  const [annee, setAnnee] = useState(aujourdhui.getFullYear());
  const [mois, setMois] = useState(aujourdhui.getMonth());

  const [fiche, setFiche] = useState(null);
  const [infos, setInfos] = useState({ nom: '', telephone_whatsapp: '', nom_crm: '' });
  const [infosEtat, setInfosEtat] = useState(null); // 'envoi' | 'ok' | message d'erreur
  const [donnees, setDonnees] = useState({ contrats: [], total_ttc: 0, nombre: 0 });
  const [chargement, setChargement] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [resultats, setResultats] = useState(null);
  const [saisie, setSaisie] = useState(null); // contrat en cours d'ajout / de modification
  const [saisieBusy, setSaisieBusy] = useState(false);
  const [erreurSaisie, setErreurSaisie] = useState('');

  // ---- Chargement ----

  const chargerFiche = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/mon-espace/fiche`, authHeader);
      setFiche(res.data);
      setInfos({
        nom: res.data.nom || '',
        telephone_whatsapp: res.data.telephone_whatsapp || '',
        nom_crm: res.data.nom_crm || ''
      });
    } catch (e) {
      setFiche(null);
    }
  }, [API_URL, authHeader]);

  const chargerContrats = useCallback(async () => {
    setChargement(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/mon-espace/contrats?mois=${cleMois(annee, mois)}`, authHeader
      );
      setDonnees(res.data);
    } catch (e) {
      setDonnees({ contrats: [], total_ttc: 0, nombre: 0 });
    } finally {
      setChargement(false);
    }
  }, [API_URL, authHeader, annee, mois]);

  useEffect(() => { chargerFiche(); }, [chargerFiche]);
  useEffect(() => { chargerContrats(); }, [chargerContrats]);

  // ---- Mes informations ----

  const enregistrerInfos = async (e) => {
    e.preventDefault();
    setInfosEtat('envoi');
    try {
      const res = await axios.put(`${API_URL}/api/mon-espace/fiche`, infos, authHeader);
      setFiche(res.data);
      setInfosEtat('ok');
      setTimeout(() => setInfosEtat(null), 2500);
    } catch (err) {
      setInfosEtat(err.response?.data?.error || 'Enregistrement impossible.');
    }
  };

  // ---- Contrats ----

  const parJour = useMemo(() => {
    const table = {};
    (donnees.contrats || []).forEach(c => {
      const cle = jourDe(c.date_contrat);
      if (!table[cle]) table[cle] = [];
      table[cle].push(c);
    });
    return table;
  }, [donnees]);

  // Grille du mois, lundi en premier, complétée par les cases vides du début.
  const cases = useMemo(() => {
    const premier = new Date(Date.UTC(annee, mois, 1));
    const decalage = (premier.getUTCDay() + 6) % 7;
    const nbJours = new Date(Date.UTC(annee, mois + 1, 0)).getUTCDate();
    const liste = [];
    for (let i = 0; i < decalage; i++) liste.push(null);
    for (let j = 1; j <= nbJours; j++) {
      liste.push(`${cleMois(annee, mois)}-${String(j).padStart(2, '0')}`);
    }
    return liste;
  }, [annee, mois]);

  const changerMois = (pas) => {
    const d = new Date(Date.UTC(annee, mois + pas, 1));
    setAnnee(d.getUTCFullYear());
    setMois(d.getUTCMonth());
    setResultats(null);
    setRecherche('');
  };

  const nouveauContrat = (date) => setSaisie({
    id: null, date_contrat: date || dateDuJour(),
    montant_ttc: '', numero_fiche: '', client_nom: '', client_telephone: ''
  });

  const modifierContrat = (c) => setSaisie({
    id: c.id,
    date_contrat: jourDe(c.date_contrat),
    montant_ttc: String(c.montant_ttc ?? ''),
    numero_fiche: c.numero_fiche || '',
    client_nom: c.client_nom || '',
    client_telephone: c.client_telephone || ''
  });

  const enregistrerContrat = async (e) => {
    e.preventDefault();
    setSaisieBusy(true);
    setErreurSaisie('');
    try {
      if (saisie.id) {
        await axios.put(`${API_URL}/api/mon-espace/contrats/${saisie.id}`, saisie, authHeader);
      } else {
        await axios.post(`${API_URL}/api/mon-espace/contrats`, saisie, authHeader);
      }
      setSaisie(null);
      await chargerContrats();
      if (resultats) lancerRecherche(recherche);
    } catch (err) {
      setErreurSaisie(err.response?.data?.error || 'Enregistrement impossible.');
    } finally {
      setSaisieBusy(false);
    }
  };

  const supprimerContrat = async (c) => {
    if (!window.confirm(`Supprimer le contrat de ${c.client_nom} du ${enFrancais(c.date_contrat)} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/mon-espace/contrats/${c.id}`, authHeader);
      await chargerContrats();
      if (resultats) lancerRecherche(recherche);
    } catch (err) {
      alert(err.response?.data?.error || 'Suppression impossible.');
    }
  };

  const lancerRecherche = async (terme) => {
    const t = String(terme || '').trim();
    if (!t) { setResultats(null); return; }
    try {
      const res = await axios.get(`${API_URL}/api/mon-espace/contrats`, {
        ...authHeader, params: { search: t }
      });
      setResultats(res.data);
    } catch (e) {
      setResultats({ contrats: [], nombre: 0, total_ttc: 0 });
    }
  };

  // ---- Rendu ----

  if (!fiche) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#5f6b85' }}>
        <style>{'@keyframes espace-spin { to { transform: rotate(360deg) } }'}</style>
        <Loader2 size={26} style={{ color: NAVY, animation: 'espace-spin 1s linear infinite' }} />
        <div style={{ marginTop: 10 }}>Chargement de votre espace…</div>
      </div>
    );
  }

  const liste = resultats ? resultats.contrats : donnees.contrats;
  // Ce qu'il touche ce mois-ci, calculé sur la règle inscrite dans SON dossier :
  // celle qu'il a acceptée, éventuellement ajustée pour lui par l'administration.
  const regle = fiche.dossier && fiche.dossier.remuneration;
  const paie = regle ? calculerPaie(regle, donnees.total_ttc) : null;

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 22 }}>
        <h2 style={{ margin: 0, color: '#fff', fontSize: 24, display: 'flex', alignItems: 'center', gap: 9 }}>
          <User size={24} style={{ color: '#fff' }} /> Mon espace
        </h2>
        <p style={{ margin: '5px 0 0', color: '#fff', fontSize: 14 }}>
          Bonjour {fiche.nom}. Vos informations et le suivi de vos contrats.
        </p>
      </div>

      {/* Mes informations */}
      <div style={carte}>
        <div style={titreCarte}><User size={17} /> Mes informations</div>
        <form onSubmit={enregistrerInfos}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 13 }}>
            <div>
              <label style={etiquette}>Nom et prénom</label>
              <input
                style={champ} type="text" value={infos.nom}
                onChange={(e) => setInfos({ ...infos, nom: e.target.value })}
              />
            </div>
            <div>
              <label style={etiquette}>Numéro WhatsApp</label>
              <input
                style={champ} type="tel" value={infos.telephone_whatsapp}
                onChange={(e) => setInfos({ ...infos, telephone_whatsapp: e.target.value })}
              />
            </div>
            <div>
              <label style={etiquette}>Mon nom dans le CRM</label>
              <input
                style={champ} type="text" value={infos.nom_crm}
                onChange={(e) => setInfos({ ...infos, nom_crm: e.target.value })}
              />
            </div>
            <div>
              <label style={etiquette}>Email de connexion</label>
              <input style={{ ...champ, background: '#f2f4f9', color: '#7a839b' }} value={fiche.email_pro} readOnly />
              <div style={{ fontSize: 12, color: '#8a94ad', marginTop: 4 }}>
                C'est votre identifiant, il ne se modifie pas ici.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            <button type="submit" disabled={infosEtat === 'envoi'} style={boutonPlein}>
              <Save size={15} /> {infosEtat === 'envoi' ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            {infosEtat === 'ok' && (
              <span style={{ color: '#2e9e5b', fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle2 size={15} /> Vos informations sont à jour
              </span>
            )}
            {infosEtat && !['ok', 'envoi'].includes(infosEtat) && (
              <span style={{ color: '#c62828', fontSize: 13.5 }}>{infosEtat}</span>
            )}
          </div>
        </form>

        {fiche.dossier && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #eef0f6', fontSize: 13.5, color: '#5f6b85' }}>
            Formule retenue : <strong style={{ color: NAVY }}>{fiche.dossier.formule_nom || '—'}</strong>
            {fiche.dossier.remuneration && (
              <> · fixe {euros(fiche.dossier.remuneration.fixe)}
                {fiche.dossier.remuneration.seuil > 0 && <> · commission au-delà de {euros(fiche.dossier.remuneration.seuil)}</>}
              </>
            )}
          </div>
        )}
      </div>

      {/* Mes contrats */}
      <div style={carte}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={titreCarte}><CalendarIcon size={17} /> Mes contrats</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => changerMois(-1)} style={boutonIcone} title="Mois précédent"><ChevronLeft size={18} /></button>
            <div style={{ fontWeight: 700, color: NAVY, minWidth: 150, textAlign: 'center' }}>
              {MOIS[mois]} {annee}
            </div>
            <button onClick={() => changerMois(1)} style={boutonIcone} title="Mois suivant"><ChevronRight size={18} /></button>
          </div>
        </div>

        <div style={{
          display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
          background: '#f5f7fd', borderRadius: 10, padding: '12px 15px', margin: '14px 0'
        }}>
          <div>
            <div style={{ fontSize: 11.5, color: '#5f6b85', fontWeight: 700, letterSpacing: 0.3 }}>TOTAL TTC DU MOIS</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: NAVY }}>{euros(donnees.total_ttc)}</div>
          </div>
          <div style={{ borderLeft: '1px solid #dde3f2', paddingLeft: 14 }}>
            <div style={{ fontSize: 11.5, color: '#5f6b85', fontWeight: 700, letterSpacing: 0.3 }}>CONTRATS</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: NAVY }}>{donnees.nombre}</div>
          </div>
          <button onClick={() => nouveauContrat()} style={{ ...boutonPlein, marginLeft: 'auto' }}>
            <Plus size={16} /> Ajouter un contrat
          </button>
        </div>

        {/* Ce que ça lui rapporte */}
        {paie && (
          <div style={{
            background: '#2d3f7f', color: '#fff', borderRadius: 11, padding: '14px 16px', marginBottom: 14
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <Wallet size={17} />
              <span style={{ fontWeight: 800, fontSize: 15 }}>Ce que vous touchez ce mois-ci</span>
              <span style={{ fontSize: 12, opacity: 0.75 }}>
                formule {fiche.dossier.formule_nom || '—'}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1 }}>{dirhams(paie.total)}</div>
              <div style={{ fontSize: 15, opacity: 0.85 }}>soit {euros(paie.total)}</div>
              <div style={{ fontSize: 11.5, opacity: 0.7 }}>conversion ×{TAUX_MAD}</div>
            </div>

            <div style={{ fontSize: 12.5, opacity: 0.9, marginTop: 9, lineHeight: 1.7 }}>
              Chiffre d'affaires TTC : {euros(donnees.total_ttc)}
              {paie.seuil > 0 && <> · part au-dessus de {euros(paie.seuil)} : {euros(paie.tranche)}</>}
              {' '}· base après TVA de {pourcent(paie.retenue)} : {euros(paie.base)}
              {' '}· commission {pourcent(paie.taux)} : <strong>{euros(paie.commission)}</strong>
              {paie.fixe > 0 && <> · fixe : {euros(paie.fixe)}</>}
            </div>

            {paie.seuil > 0 && paie.tranche === 0 && (
              <div style={{
                background: 'rgba(255,255,255,0.14)', borderRadius: 8, padding: '8px 11px',
                marginTop: 9, fontSize: 12.5
              }}>
                Le seuil de {euros(paie.seuil)} n'est pas encore atteint : pour l'instant, seul le fixe est dû.
                Il vous manque {euros(Math.max(0, paie.seuil - (Number(donnees.total_ttc) || 0)))} de chiffre d'affaires.
              </div>
            )}

            <div style={{ fontSize: 11.5, opacity: 0.7, marginTop: 8 }}>
              Calcul indicatif, établi sur les contrats saisis ci-dessous. Le montant versé est arrêté par l'administration.
            </div>
          </div>
        )}

        {/* Calendrier */}
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 560 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 6 }}>
              {JOURS.map(j => (
                <div key={j} style={{ textAlign: 'center', fontSize: 11.5, fontWeight: 700, color: '#5f6b85', textTransform: 'uppercase' }}>{j}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
              {cases.map((date, i) => {
                if (!date) return <div key={`vide-${i}`} />;
                const duJour = parJour[date] || [];
                const somme = duJour.reduce((t, c) => t + Number(c.montant_ttc || 0), 0);
                const estAujourdhui = date === dateDuJour();
                return (
                  <button
                    key={date}
                    onClick={() => nouveauContrat(date)}
                    title={duJour.length ? `${duJour.length} contrat(s) — ${euros(somme)}` : 'Ajouter un contrat ce jour'}
                    style={{
                      minHeight: 68, padding: 6, textAlign: 'left', cursor: 'pointer',
                      border: `1.5px solid ${estAujourdhui ? NAVY : '#e7eaf3'}`,
                      background: duJour.length ? '#eef2fb' : '#fff',
                      borderRadius: 9, display: 'flex', flexDirection: 'column', gap: 4
                    }}
                  >
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: estAujourdhui ? NAVY : '#5f6b85' }}>
                      {Number(date.slice(8, 10))}
                    </span>
                    {duJour.length > 0 && (
                      <>
                        <span style={{ fontSize: 13, fontWeight: 800, color: NAVY }}>{euros(somme)}</span>
                        <span style={{ fontSize: 11, color: '#5f6b85' }}>
                          {duJour.length} contrat{duJour.length > 1 ? 's' : ''}
                        </span>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recherche client */}
        <div style={{ display: 'flex', gap: 9, alignItems: 'center', margin: '18px 0 12px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 260px' }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: 12, color: '#8a94ad' }} />
            <input
              style={{ ...champ, paddingLeft: 34 }}
              placeholder="Retrouver un client : nom, téléphone ou numéro de fiche"
              value={recherche}
              onChange={(e) => { setRecherche(e.target.value); lancerRecherche(e.target.value); }}
            />
          </div>
          {resultats && (
            <button onClick={() => { setRecherche(''); setResultats(null); }} style={boutonVide}>
              <X size={14} /> Voir le mois
            </button>
          )}
        </div>

        {/* Liste */}
        {chargement ? (
          <div style={{ padding: 26, textAlign: 'center', color: '#5f6b85' }}>Chargement…</div>
        ) : liste.length === 0 ? (
          <div style={{ padding: 26, textAlign: 'center', color: '#8a94ad' }}>
            {resultats ? 'Aucun contrat ne correspond à cette recherche.' : 'Aucun contrat ce mois-ci. Cliquez sur un jour pour en ajouter un.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Client</th>
                  <th style={th}>Téléphone</th>
                  <th style={th}>N° de fiche</th>
                  <th style={{ ...th, textAlign: 'right' }}>Montant TTC</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {liste.map(c => (
                  <tr key={c.id} style={{ borderTop: '1px solid #f0f2f7' }}>
                    <td style={td}>{enFrancais(c.date_contrat)}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{c.client_nom}</td>
                    <td style={td}>
                      {c.client_telephone
                        ? <a href={`tel:${c.client_telephone}`} style={{ color: '#2e9e5b', textDecoration: 'none' }}>{c.client_telephone}</a>
                        : <span style={{ color: '#c9cfe0' }}>—</span>}
                    </td>
                    <td style={td}>{c.numero_fiche || <span style={{ color: '#c9cfe0' }}>—</span>}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: NAVY }}>{euros(c.montant_ttc)}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => modifierContrat(c)} title="Modifier" style={boutonIcone}><Pencil size={15} /></button>
                      <button onClick={() => supprimerContrat(c)} title="Supprimer" style={{ ...boutonIcone, color: '#c62828' }}><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {resultats && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #e7eaf3' }}>
                    <td style={{ ...td, fontWeight: 700 }} colSpan={4}>
                      {resultats.nombre} contrat{resultats.nombre > 1 ? 's' : ''} trouvé{resultats.nombre > 1 ? 's' : ''}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: NAVY }}>{euros(resultats.total_ttc)}</td>
                    <td style={td} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Saisie d'un contrat */}
      {saisie && (
        <div style={fondModale} onClick={() => setSaisie(null)}>
          <div style={modale} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: NAVY, fontSize: 19 }}>
                {saisie.id ? 'Modifier le contrat' : 'Nouveau contrat'}
              </h3>
              <button onClick={() => setSaisie(null)} style={boutonIcone}><X size={20} /></button>
            </div>

            <form onSubmit={enregistrerContrat}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={etiquette}><CalendarIcon size={12} /> Date du contrat</label>
                  <input
                    style={champ} type="date" required value={saisie.date_contrat}
                    onChange={(e) => setSaisie({ ...saisie, date_contrat: e.target.value })}
                  />
                </div>
                <div>
                  <label style={etiquette}><Euro size={12} /> Montant TTC</label>
                  <input
                    style={champ} type="text" inputMode="decimal" required placeholder="1 200,50"
                    value={saisie.montant_ttc}
                    onChange={(e) => setSaisie({ ...saisie, montant_ttc: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <label style={etiquette}><FileText size={12} /> Numéro de fiche</label>
                <input
                  style={champ} type="text" placeholder="F-001" value={saisie.numero_fiche}
                  onChange={(e) => setSaisie({ ...saisie, numero_fiche: e.target.value })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div>
                  <label style={etiquette}><User size={12} /> Nom du client</label>
                  <input
                    style={champ} type="text" required placeholder="Jean Dupont" value={saisie.client_nom}
                    onChange={(e) => setSaisie({ ...saisie, client_nom: e.target.value })}
                  />
                </div>
                <div>
                  <label style={etiquette}><Phone size={12} /> Téléphone du client</label>
                  <input
                    style={champ} type="tel" placeholder="06 12 34 56 78" value={saisie.client_telephone}
                    onChange={(e) => setSaisie({ ...saisie, client_telephone: e.target.value })}
                  />
                </div>
              </div>

              {erreurSaisie && (
                <div style={{
                  background: '#fdf0f0', border: '1px solid #e0a3a3', color: '#c62828',
                  borderRadius: 9, padding: '10px 13px', marginTop: 13, fontSize: 13.5
                }}>{erreurSaisie}</div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button type="submit" disabled={saisieBusy} style={boutonPlein}>
                  <Save size={15} /> {saisieBusy ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button type="button" onClick={() => setSaisie(null)} style={boutonVide}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const carte = {
  background: '#fff', borderRadius: 13, padding: 20, marginBottom: 20,
  boxShadow: '0 1px 3px rgba(20,30,60,0.08)'
};
const titreCarte = {
  display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800,
  color: NAVY, fontSize: 17, marginBottom: 14
};
const etiquette = {
  display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5,
  fontWeight: 700, color: NAVY, marginBottom: 5
};
const champ = {
  width: '100%', padding: '11px 13px', border: '1.5px solid #ddd',
  borderRadius: 8, fontSize: 15, boxSizing: 'border-box', background: '#fff'
};
const boutonPlein = {
  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px',
  background: NAVY, color: '#fff', border: 'none', borderRadius: 8,
  cursor: 'pointer', fontSize: 14.5, fontWeight: 600
};
const boutonVide = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px',
  background: '#fff', color: '#5f6b85', border: '1.5px solid #ddd',
  borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600
};
const boutonIcone = {
  background: 'none', border: 'none', cursor: 'pointer', color: NAVY,
  padding: 6, display: 'inline-flex', alignItems: 'center'
};
const th = {
  padding: '11px 12px', textAlign: 'left', fontSize: 11.5, color: '#5f6b85',
  fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase'
};
const td = { padding: '12px', verticalAlign: 'middle', color: '#2f3648', fontSize: 14 };
const fondModale = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
  alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 20, overflowY: 'auto'
};
const modale = {
  background: '#fff', borderRadius: 13, padding: 22, width: '100%', maxWidth: 520, marginTop: 40
};

export default EspaceCandidat;
